import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Popover,
  Select,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import LinkIcon from '@material-ui/icons/Link';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { useDispatch } from 'react-redux';
import { searchMerchandisingValues } from '../state/merchandisingSlice';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { useMerchandisingWorkbenchCopy } from './useMerchandisingWorkbenchCopy';
import {
  SearchInput,
  SelectionCheckbox,
  ProductImage,
} from './MerchandisingWorkbenchUi';
import SchemaValueSelector from './SchemaValueSelector';
import { runWithConcurrency } from '../model/rulePortability';
import { merchandisingRegexError } from '../model/regexValidation';
import { REGEX_OPERATORS as regexOperators } from '../model/ruleCapabilities';
import {
  multipleOperators,
  isNumericCondition,
  descriptorForSavedCondition,
  conditionOperatorOptions,
  conditionValueForOperatorChange,
  humanField,
  humanOperator,
  groupConditionOperatorOptions,
  fallbackConditionValueLabel,
} from '../model/conditionEditor';

let controlSequence = 0;
const uuid = () => ++controlSequence;

export const LogicToggle = ({ value, onChange }) => {
  const t = useLocalizedMessage();
  return (
    <div
      className={`mp-condition-connector ${
        value === 'any' ? 'is-any' : 'is-all'
      }`}
      role="group"
      aria-label={t('merchandising.workbench.logic.label')}
    >
      <button
        type="button"
        className={value === 'all' ? 'is-active' : ''}
        aria-pressed={value === 'all'}
        onClick={() => onChange('all')}
      >
        {t('merchandising.workbench.logic.and')}
      </button>
      <button
        type="button"
        className={value === 'any' ? 'is-active' : ''}
        aria-pressed={value === 'any'}
        onClick={() => onChange('any')}
      >
        {t('merchandising.workbench.logic.or')}
      </button>
    </div>
  );
};
LogicToggle.propTypes = {
  value: PropTypes.oneOf(['all', 'any']).isRequired,
  onChange: PropTypes.func.isRequired,
};

const ProductConditionPicker = ({
  multiple,
  onChange,
  productApi,
  selectedIds,
  onItems,
}) => {
  const t = useLocalizedMessage();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let current = true;
    if (!query.trim()) {
      setItems([]);
      setLoading(false);
      return () => {
        current = false;
      };
    }
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const result = await productApi.searchProducts(query.trim(), 20);
        if (!current) return;
        const next = Array.isArray(result) ? result : result?.items || [];
        setItems(next);
        onItems(next);
      } catch (error) {
        if (current && error) setItems([]);
      } finally {
        if (current) setLoading(false);
      }
    }, 250);
    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [onItems, productApi, query]);

  const toggle = id => {
    const normalized = String(id);
    if (selectedIds.includes(normalized)) {
      onChange(
        multiple ? selectedIds.filter(entry => entry !== normalized) : ''
      );
      return;
    }
    onChange(multiple ? [...selectedIds, normalized] : normalized);
  };

  return (
    <div className="mp-workbench-value-selector">
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder={t('product.selector.search.placeholder')}
      />
      <div
        className="mp-value-picker"
        role="group"
        aria-label={t('merchandising.selector.results')}
        aria-busy={loading}
      >
        {items.map(product => {
          const id = String(product.id);
          const selected = selectedIds.includes(id);
          return (
            <label
              key={id}
              className={`mp-value-option mp-value-option--product ${
                selected ? 'is-selected' : ''
              }`}
            >
              <SelectionCheckbox
                checked={selected}
                label={product.name || `#${id}`}
                onChange={() => toggle(id)}
              />
              <ProductImage product={product} size="thumb" />
              <span className="mp-value-option-copy">
                <strong>{product.name || `#${id}`}</strong>
                <small>
                  {product.reference || product.vendorCode || `#${id}`}
                </small>
              </span>
            </label>
          );
        })}
        {!loading && items.length === 0 && (
          <div className="mp-value-picker-empty">
            {t('merchandising.selector.empty')}
          </div>
        )}
      </div>
    </div>
  );
};
ProductConditionPicker.propTypes = {
  multiple: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  productApi: PropTypes.object.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onItems: PropTypes.func.isRequired,
};

export const ConditionValue = ({
  condition,
  descriptor,
  onChange,
  onItems,
  productApi,
  languageId = null,
}) => {
  const dispatch = useDispatch();
  const t = useLocalizedMessage();
  const C = useMerchandisingWorkbenchCopy();
  const [open, setOpen] = useState(false);
  const [labels, setLabels] = useState({});
  const multiple = multipleOperators.has(condition.operator);
  const textContains = [
    'text_contains_any',
    'text_not_contains_any',
    'text_contains_any_case',
  ].includes(condition.operator);
  const selectorField = descriptor?.key || condition.field;
  const sourceAction = textContains ? null : descriptor?.value_source?.action;
  const values = multiple
    ? Array.isArray(condition.value)
      ? condition.value
      : []
    : condition.value;
  const selectedIds = (Array.isArray(values) ? values : [values]).filter(
    value => value !== ''
  );
  const rememberItems = useCallback(
    items => {
      setLabels(current => ({
        ...current,
        ...Object.fromEntries(
          items.map(item => [
            String(item.id),
            item.label ||
              item.name ||
              item.breadcrumb ||
              fallbackConditionValueLabel(item.id, sourceAction),
          ])
        ),
      }));
      onItems(items);
    },
    [onItems, sourceAction]
  );
  const removeValue = id =>
    onChange(multiple ? selectedIds.filter(value => value !== id) : '');
  const showAsChip = sourceAction || descriptor?.value_type === 'boolean';
  const numeric = isNumericCondition(condition, descriptor);
  const compactLiteralList =
    !showAsChip &&
    Array.isArray(condition.value) &&
    condition.operator !== 'between';
  const compactValues = textContains || compactLiteralList;
  const selectedFingerprint = selectedIds.map(String).join('|');
  const labelsScope = useRef(null);

  useEffect(() => {
    const scope = JSON.stringify([languageId, selectorField, sourceAction]);
    const scopeChanged = labelsScope.current !== scope;
    labelsScope.current = scope;
    if (scopeChanged && Object.keys(labels).length) setLabels({});
    const missing = selectedIds
      .map(String)
      .filter(id => scopeChanged || !labels[id]);
    if (!sourceAction || !missing.length) return undefined;
    let current = true;
    const resolve = async () => {
      if (sourceAction === 'product_selector.search') {
        const response = await productApi.resolveProducts(missing);
        if (current)
          rememberItems(
            Array.isArray(response) ? response : response?.items || []
          );
        return;
      }
      const responses = await runWithConcurrency(
        missing,
        id =>
          dispatch(
            searchMerchandisingValues({
              action: sourceAction,
              field: selectorField,
              query: id,
              limit: 20,
              languageId,
            })
          ),
        { concurrency: 3, isCancelled: () => !current }
      );
      if (!current) return;
      const items = responses.flatMap(result => {
        const action = result.status === 'succeeded' ? result.value : null;
        return action && searchMerchandisingValues.fulfilled.match(action)
          ? action.payload?.items || []
          : [];
      });
      rememberItems(items.filter(item => missing.includes(String(item.id))));
    };
    resolve().catch(() => {});
    return () => {
      current = false;
    };
    // Resolve labels by selected identities and catalog scope; cap remote fan-out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    languageId,
    productApi,
    selectedFingerprint,
    selectorField,
    sourceAction,
  ]);

  if (condition.operator === 'exists') return null;

  if (regexOperators.has(condition.operator)) {
    const error = merchandisingRegexError(condition.value);
    const errorMessage = error
      ? t(`merchandising.condition.regex.${error}`)
      : null;
    return (
      <label
        className={`mp-condition-number-single mp-condition-regex ${
          error ? 'is-invalid' : ''
        }`}
      >
        <input
          aria-invalid={Boolean(error)}
          aria-label={t('merchandising.condition.regex.ariaLabel', {
            field: humanField(condition.field, descriptor),
          })}
          type="text"
          maxLength={200}
          spellCheck={false}
          placeholder={t('merchandising.condition.regex.placeholder')}
          value={condition.value}
          onChange={event => onChange(event.target.value)}
        />
        {errorMessage && (
          <span className="mp-condition-regex-error" role="alert">
            {errorMessage}
          </span>
        )}
      </label>
    );
  }

  if (numeric) {
    const range =
      condition.operator === 'between'
        ? Array.isArray(condition.value)
          ? condition.value
          : ['', '']
        : null;
    const numericValue = value => (value === '' ? '' : Number(value));
    const rangeInvalid =
      range &&
      range.every(value => value !== '' && Number.isFinite(Number(value))) &&
      Number(range[0]) > Number(range[1]);
    return condition.operator === 'between' ? (
      <div
        className={`mp-condition-number-range ${
          rangeInvalid ? 'is-invalid' : ''
        }`}
      >
        <label>
          <span>{t('merchandising.condition.from')}</span>
          <input
            aria-invalid={rangeInvalid}
            aria-label={`${humanField(condition.field, descriptor)} ${t(
              'merchandising.condition.from'
            )}`}
            type="number"
            inputMode="decimal"
            value={range[0]}
            onChange={event =>
              onChange([numericValue(event.target.value), range[1]])
            }
          />
        </label>
        <span className="mp-condition-range-separator" aria-hidden="true">
          —
        </span>
        <label>
          <span>{t('merchandising.condition.to')}</span>
          <input
            aria-invalid={rangeInvalid}
            aria-label={`${humanField(condition.field, descriptor)} ${t(
              'merchandising.condition.to'
            )}`}
            type="number"
            inputMode="decimal"
            value={range[1]}
            onChange={event =>
              onChange([range[0], numericValue(event.target.value)])
            }
          />
        </label>
        {rangeInvalid && (
          <span className="mp-condition-range-error" role="alert">
            {t('merchandising.condition.rangeOrder')}
          </span>
        )}
      </div>
    ) : (
      <label className="mp-condition-number-single">
        <input
          aria-label={`${humanField(condition.field, descriptor)} ${t(
            'merchandising.condition.value'
          )}`}
          type="number"
          inputMode="decimal"
          value={condition.value}
          onChange={event => onChange(numericValue(event.target.value))}
        />
      </label>
    );
  }

  return (
    <div className="mp-condition-values">
      {showAsChip &&
        selectedIds.map(id => (
          <Chip
            key={id}
            label={
              labels[String(id)] ||
              (descriptor?.value_type === 'boolean'
                ? t(`merchandising.value.${String(id)}`)
                : fallbackConditionValueLabel(id, sourceAction))
            }
            onDelete={() => removeValue(id)}
          />
        ))}
      {!showAsChip &&
        condition.operator === 'between' &&
        Array.isArray(condition.value) && (
          <Chip
            label={`${condition.value[0]} ${t(
              'merchandising.workbench.rangeTo'
            )} ${condition.value[1]}`}
          />
        )}
      {compactValues &&
        selectedIds
          .slice(0, 3)
          .map(value => (
            <Chip
              key={String(value)}
              label={String(value)}
              onDelete={() => removeValue(value)}
            />
          ))}
      {compactValues && selectedIds.length > 3 && (
        <Chip
          label={`+${selectedIds.length - 3}`}
          onClick={() => setOpen(true)}
          aria-label={`${t('merchandising.workbench.chooseValues')} · ${
            selectedIds.length
          }`}
        />
      )}
      {!showAsChip &&
        !compactValues &&
        condition.operator !== 'between' &&
        condition.value !== '' && <Chip label={String(condition.value)} />}
      <Button
        aria-label={`${t('merchandising.workbench.addValue')} ${humanField(
          condition.field,
          descriptor
        )}`}
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setOpen(true)}
      >
        {t('merchandising.workbench.addValue')}
      </Button>
      <Dialog
        className="mp-dialog"
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="mp-dialog-title-row">
          <span>
            {t('merchandising.workbench.chooseValues')} ·{' '}
            {humanField(condition.field, descriptor)}
          </span>
          <IconButton aria-label={C.CLOSE} onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {sourceAction === 'product_selector.search' && (
            <ProductConditionPicker
              multiple={multiple}
              selectedIds={selectedIds.map(String)}
              onChange={onChange}
              productApi={productApi}
              onItems={rememberItems}
            />
          )}
          {sourceAction && sourceAction !== 'product_selector.search' && (
            <SchemaValueSelector
              action={sourceAction}
              field={selectorField}
              languageId={languageId}
              value={values}
              multiple={multiple}
              onChange={onChange}
              onItems={rememberItems}
              presentation="workbench"
            />
          )}
          {!sourceAction && descriptor?.value_type === 'boolean' && (
            <div className="mp-value-picker">
              {[true, false].map(value => {
                const selected = Boolean(condition.value) === value;
                const label = t(
                  value
                    ? 'merchandising.value.true'
                    : 'merchandising.value.false'
                );
                return (
                  <label
                    key={String(value)}
                    className={`mp-value-option ${
                      selected ? 'is-selected' : ''
                    }`}
                  >
                    <SelectionCheckbox
                      checked={selected}
                      label={label}
                      onChange={() => onChange(value)}
                    />
                    <span className="mp-value-option-copy">
                      <strong>{label}</strong>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          {!sourceAction &&
            descriptor?.value_type !== 'boolean' &&
            descriptor?.value_type !== 'number' && (
              <label className="mp-dialog-value-field">
                <span>{t('merchandising.condition.value')}</span>
                <input
                  value={Array.isArray(values) ? values.join(', ') : values}
                  onChange={event =>
                    onChange(
                      multiple
                        ? event.target.value
                            .split(',')
                            .map(value => value.trim())
                            .filter(Boolean)
                        : event.target.value
                    )
                  }
                />
              </label>
            )}
        </DialogContent>
        <DialogActions className="mp-dialog-actions">
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpen(false)}
          >
            {C.DONE}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
ConditionValue.propTypes = {
  condition: PropTypes.object.isRequired,
  descriptor: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onItems: PropTypes.func,
  productApi: PropTypes.object.isRequired,
};
ConditionValue.defaultProps = { descriptor: null, onItems: () => {} };

export const OperatorPicker = ({ label, onChange, options, value }) => {
  const t = useLocalizedMessage();
  const [anchorEl, setAnchorEl] = useState(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef({});
  const [pickerId] = useState(() => `mp-operator-picker-${uuid()}`);
  const groups = groupConditionOperatorOptions(options);
  const flatOptions = groups.flatMap(group => group.options);
  const selected = options.find(option => option.value === value) || options[0];
  const open = Boolean(anchorEl);

  const close = (restoreFocus = false) => {
    setAnchorEl(null);
    if (restoreFocus)
      window.setTimeout(() => {
        if (triggerRef.current) triggerRef.current.focus();
      }, 0);
  };
  const focusSelected = () => {
    const target =
      optionRefs.current[value] || optionRefs.current[flatOptions[0]?.value];
    if (target) target.focus();
  };
  const openPicker = event => setAnchorEl(event.currentTarget);
  const select = selection => {
    if (selection !== value) onChange(selection);
    close(true);
  };
  const handleTriggerKeyDown = event => {
    if (!['ArrowDown', 'Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };
  const handlePickerKeyDown = event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const currentIndex = flatOptions.findIndex(
      option => optionRefs.current[option.value] === document.activeElement
    );
    let nextIndex = 0;
    if (event.key === 'End') nextIndex = flatOptions.length - 1;
    else if (event.key === 'ArrowUp') {
      nextIndex = currentIndex <= 0 ? flatOptions.length - 1 : currentIndex - 1;
    } else if (event.key === 'ArrowDown') {
      nextIndex =
        currentIndex < 0 || currentIndex === flatOptions.length - 1
          ? 0
          : currentIndex + 1;
    }
    const nextOption = flatOptions[nextIndex];
    const nextTarget = nextOption && optionRefs.current[nextOption.value];
    if (nextTarget) nextTarget.focus();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`mp-condition-operator mp-condition-operator-trigger ${
          open ? 'is-open' : ''
        }`}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? pickerId : undefined}
        title={selected?.label}
        onClick={openPicker}
        onKeyDown={handleTriggerKeyDown}
      >
        <span>{selected?.label}</span>
        <ArrowDropDownIcon aria-hidden="true" />
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => close(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          className: `mp-operator-picker-paper has-${Math.min(
            groups.length,
            4
          )}-columns`,
        }}
        TransitionProps={{ onEntered: focusSelected }}
      >
        <div
          id={pickerId}
          className="mp-operator-picker"
          role="listbox"
          aria-label={label}
          onKeyDown={handlePickerKeyDown}
        >
          {groups.map(({ group, options: groupOptions }) => {
            const headingId = `${pickerId}-${group}`;
            return (
              <section
                key={group}
                className="mp-operator-group"
                role="group"
                aria-labelledby={headingId}
              >
                <strong id={headingId}>
                  {t(`merchandising.condition.operatorGroup.${group}`)}
                </strong>
                <div>
                  {groupOptions.map(option => (
                    <button
                      key={option.value}
                      ref={node => {
                        optionRefs.current[option.value] = node;
                      }}
                      type="button"
                      role="option"
                      aria-selected={option.value === value}
                      className={option.value === value ? 'is-selected' : ''}
                      onClick={() => select(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </Popover>
    </>
  );
};
OperatorPicker.propTypes = {
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      group: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.string.isRequired,
};

export const ConditionRow = ({
  condition,
  schema,
  productApi,
  dragging,
  dropTarget,
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDrop,
  onItems,
  onRemove,
  onUpdate,
  advancedConditions,
  draggable,
  languageId,
  extra,
  onGroup,
}) => {
  const t = useLocalizedMessage();
  const descriptor = descriptorForSavedCondition(
    condition,
    schema,
    advancedConditions
  );
  const operatorOptions = conditionOperatorOptions(
    descriptor,
    condition.operator,
    t,
    advancedConditions
  );
  const selectedOperator =
    condition.operator === 'exists'
      ? `exists:${condition.value !== false}`
      : condition.operator;
  const selectOperator = selection => {
    const operator = selection.startsWith('exists:') ? 'exists' : selection;
    onUpdate({
      operator,
      value: conditionValueForOperatorChange(condition, descriptor, selection),
    });
  };
  return (
    <div
      className={`mp-condition-row ${
        isNumericCondition(condition, descriptor) ? 'is-numeric' : ''
      } ${condition.operator === 'exists' ? 'is-unary' : ''} ${
        !draggable ? 'is-static' : ''
      } ${dragging ? 'is-dragging' : ''} ${
        dropTarget ? 'is-group-target' : ''
      }`}
      draggable={draggable}
      onDragStart={event => onDragStart(event, condition.__id)}
      onDragOver={event => event.preventDefault()}
      onDragEnter={() => onDragEnter(condition.__id)}
      onDragEnd={onDragEnd}
      onDrop={event => onDrop(event, condition.__id)}
    >
      {draggable && (
        <span className="mp-condition-drag" aria-hidden="true">
          <DragIndicatorIcon />
        </span>
      )}
      <div className="mp-condition-content">
        <strong className="mp-condition-title">
          {humanField(condition.field, descriptor)}
        </strong>
        <div className="mp-condition-controls">
          {operatorOptions.length > 1 ? (
            <OperatorPicker
              value={selectedOperator}
              label={t('merchandising.condition.operator')}
              options={operatorOptions}
              onChange={selectOperator}
            />
          ) : (
            <span className="mp-condition-operator-static">
              {operatorOptions[0]?.label || humanOperator(condition.operator)}
            </span>
          )}
          <ConditionValue
            languageId={languageId}
            condition={condition}
            descriptor={descriptor}
            productApi={productApi}
            onItems={onItems}
            onChange={value => onUpdate({ value })}
          />
        </div>
        {extra}
      </div>
      <div className="mp-condition-actions">
        {onGroup && (
          <IconButton
            aria-label={t('merchandising.condition.groupWithPrevious')}
            title={t('merchandising.condition.groupWithPrevious')}
            onClick={onGroup}
          >
            <LinkIcon />
          </IconButton>
        )}
        <IconButton
          aria-label={t('merchandising.workbench.removeFilter', {
            name: humanField(condition.field, descriptor),
          })}
          onClick={onRemove}
        >
          <DeleteOutlineIcon />
        </IconButton>
      </div>
      {dropTarget && (
        <span className="mp-condition-drop-hint">
          {t('merchandising.workbench.groupFilterHint')}
        </span>
      )}
    </div>
  );
};
ConditionRow.propTypes = {
  condition: PropTypes.object.isRequired,
  schema: PropTypes.object.isRequired,
  productApi: PropTypes.object.isRequired,
  dragging: PropTypes.bool.isRequired,
  dropTarget: PropTypes.bool.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onDragEnter: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onItems: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  advancedConditions: PropTypes.bool,
  draggable: PropTypes.bool,
  languageId: PropTypes.string,
  extra: PropTypes.node,
  onGroup: PropTypes.func,
};
ConditionRow.defaultProps = {
  advancedConditions: true,
  draggable: true,
  languageId: null,
  extra: null,
  onGroup: null,
};
